import React from 'react'

import PropTypes from 'prop-types'

import LottieLoader from '@/ui/LottieLoader'
import TextFormatted from '@/ui/TextFormatted'
import Tooltip from '@/ui/Tooltip'

import { ButtonStyles } from './Button.styles'
import { iconButtonVariants } from './definitions'

import { ArrowOK } from '@/icons'
import { sizePropsTypes } from '@/theme/primaryTheme'

const SUCCESS_VARIANT = 'success'
const IN_CIRCLE_VARIANT = 'inCircle'
const SECONDARY_VARIANT = 'secondary'

const Button = ({
  startIcon,
  endIcon,
  children,
  size = 'md',
  variant = 'primary',
  isSuccess,
  isLoading,
  shouldShowShadow,
  isDisabled,
  labelId,
  label,
  textVariant,
  textValues,
  textColor,
  fullWidth,
  onClick,
  textProps,
  prefix,
  tooltipMessage,
  tooltipMessageId,
  visible = true,
  ...props
}) => {
  const getRealSize = () => {
    return size ?? iconButtonVariants?.[variant]
  }

  const getRealVariant = () => {
    if (isSuccess) return SUCCESS_VARIANT
    if (isLoading) return SECONDARY_VARIANT
    return variant
  }

  const getRealEndIcon = () => {
    if (isLoading) return <LottieLoader size='1.25rem' />
    if (isSuccess) return <ArrowOK />
    return endIcon
  }

  const getRealDisabled = () => {
    return isSuccess || isLoading || isDisabled
  }

  const realSize = getRealSize()
  const realVariant = getRealVariant()
  const realEndIcon = getRealEndIcon()
  const realDisabled = getRealDisabled()

  const renderButtonContent = () => {
    if (realVariant === IN_CIRCLE_VARIANT) {
      return (
        <>
          {startIcon}
          {realEndIcon}
        </>
      )
    } else {
      return (
        <>
          {startIcon}
          {prefix}
          {(labelId || label) && (
            <TextFormatted
              labelId={labelId}
              label={label}
              values={textValues}
              variant={textVariant}
              color={textColor}
              {...textProps}
            />
          )}
          {children}
          {realEndIcon}
        </>
      )
    }
  }

  return (
    <Tooltip message={tooltipMessage} messageId={tooltipMessageId}>
      <ButtonStyles
        variant={realVariant}
        size={realSize}
        $shouldShowShadow={shouldShowShadow}
        disabled={realDisabled}
        onClick={(e) => onClick?.(e)}
        $fullWidth={fullWidth}
        visible={visible}
        {...props}
      >
        {renderButtonContent()}
      </ButtonStyles>
    </Tooltip>
  )
}

export const ButtonPropsTypes = {
  startIcon: PropTypes.node,
  shouldShowShadow: PropTypes.bool,
  fullWidth: PropTypes.bool,
  endIcon: PropTypes.node,
  children: PropTypes.node,
  prefix: PropTypes.node,
  size: sizePropsTypes,
  variant: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  isSuccess: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelId: PropTypes.string,
  textVariant: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  textColor: PropTypes.string,
  textValues: PropTypes.object,
  props: PropTypes.any,
  onClick: PropTypes.func,
  textProps: PropTypes.object,
  tooltipMessage: PropTypes.string,
  tooltipMessageId: PropTypes.string,
  visible: PropTypes.bool,
}

Button.propTypes = ButtonPropsTypes

export default Button
